import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeedbackFooter from "@/components/FeedbackFooter";

const EFFECTIVE_DATE = "August 2026";

const Section = ({ title, children }) => (
    <section className="mb-10">
        <h2 className="font-heading text-2xl font-[700] tracking-tight mb-4">{title}</h2>
        <div className="space-y-4 text-muted-foreground leading-relaxed">{children}</div>
    </section>
);

export default function Terms() {
    return (
        <div className="min-h-screen relative overflow-hidden bg-background">
            <div className="blob blob-cream w-[500px] h-[500px] -top-40 -right-40" />
            <div className="blob blob-peach w-[500px] h-[500px] top-1/2 -left-40 opacity-40" />

            <nav className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
                <Link to="/" className="flex items-center gap-2" data-testid="terms-brand-link">
                    <div className="w-9 h-9 rounded-2xl bg-primary/90 flex items-center justify-center shadow-soft">
                        <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                    </div>
                    <span className="font-heading text-2xl font-[800] tracking-tight">CheerUp!</span>
                </Link>
                <Link to="/" data-testid="terms-back-link">
                    <Button variant="ghost" className="rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back home
                    </Button>
                </Link>
            </nav>

            <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="relative z-10 max-w-3xl mx-auto px-6 sm:px-12 pb-24 pt-6"
                data-testid="terms-page"
            >
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">Terms of Service</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight mb-4">
                    The gentle fine print.
                </h1>
                <p className="text-muted-foreground mb-2">Effective {EFFECTIVE_DATE}</p>
                <p className="text-muted-foreground mb-12 leading-relaxed">
                    Welcome to CheerUp! ("we", "our", "us"). By creating an account or using CheerUp! (the
                    "Service"), you agree to these Terms. We've tried to keep them short, honest, and human.
                </p>

                <Section title="1. Who can use CheerUp!">
                    <p>You must be at least <strong>18 years old</strong> to create an account and use CheerUp!. By signing up you confirm that you are 18 or older, that the information you provide is accurate, and that you have the right to accept these Terms. Accounts found to belong to individuals under 18 will be closed.</p>
                    <p><strong>Parent-supervised exception.</strong> If, in an exigent or supportive scenario, a person under 18 needs to access CheerUp!, this is permitted <em>only</em> through a <strong>parent-supervised account</strong>: a parent or legal guardian (18+) must create the account, remain the account holder, provide continuous supervision of every interaction, take full responsibility for the content and safety of the sessions, and be reachable at the account's email address. Independent use by minors is not allowed under any circumstance. If we learn an account is being used outside of this arrangement, we may close it.</p>
                </Section>

                <Section title="2. Your account">
                    <ul className="list-disc pl-6 space-y-2">
                        <li>You are responsible for keeping your login credentials safe and for everything that happens under your account.</li>
                        <li>Please use a strong, unique password. Let us know right away if you suspect unauthorized access.</li>
                        <li>You may delete your account at any time. See our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> for how deletion is handled.</li>
                    </ul>
                </Section>

                <Section title="3. What CheerUp! is — and isn't">
                    <p>CheerUp! is a wellness companion offering mood tracking, uplifting affirmations, guided breathing, and empathetic AI conversation. It is designed to help you feel a little lighter on hard days.</p>
                    <p><strong>CheerUp! is not a medical device, therapist, or crisis service.</strong> The Service does not diagnose, treat, or cure any condition and is not a substitute for professional mental-health care. If you are in crisis or thinking about harming yourself, please contact a local helpline immediately — in the US call or text <strong>988</strong>, or visit <a href="https://findahelpline.com" target="_blank" rel="noreferrer" className="text-primary underline">findahelpline.com</a>.</p>
                </Section>

                <Section title="4. Acceptable use">
                    <p>Please do not use CheerUp! to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>Break any law or infringe anyone's rights.</li>
                        <li>Attempt to jailbreak, misuse, or extract the AI model, or to impersonate CheerUp! or another user.</li>
                        <li>Send content that harasses, threatens, or targets someone else, or that is unlawful, hateful, or sexually explicit involving minors.</li>
                        <li>Try to disrupt, reverse-engineer, or overload the Service.</li>
                        <li>Scrape, resell, or use the Service or its outputs to build a competing product.</li>
                    </ul>
                    <p>We may suspend or terminate accounts that violate these rules.</p>
                </Section>

                <Section title="5. Your content">
                    <p>You own the mood entries, notes, favorites, and messages you create ("Your Content"). By using the Service you grant us a limited, worldwide, royalty-free license to store, display, and process Your Content solely to operate and improve CheerUp! for you (for example, showing your mood history back to you, or sending your prompts to our AI provider to generate a reply).</p>
                    <p>You are responsible for Your Content. Please do not share sensitive information about other people without their permission.</p>
                </Section>

                <Section title="6. AI-generated content">
                    <p>The AI companion may occasionally produce responses that are inaccurate, incomplete, or unexpected. Treat AI messages as a caring perspective, not medical, legal, or professional advice. You use AI outputs at your own discretion.</p>
                </Section>

                <Section title="7. Third-party services">
                    <p>CheerUp! uses third-party providers (including Anthropic for AI responses and, if you choose, Google for sign-in). Your use of those integrations is also subject to their terms.</p>
                </Section>

                <Section title="8. Pricing and changes">
                    <p>CheerUp! is currently free to use. If we introduce paid plans in the future, we'll clearly communicate pricing before you're charged. We may add, change, or discontinue features from time to time to keep the Service healthy.</p>
                </Section>

                <Section title="9. Termination">
                    <p>You may stop using CheerUp! and delete your account at any time. We may suspend or terminate accounts that violate these Terms or harm the Service or its community. On termination, sections that by their nature should survive (like disclaimers, liability limits, and dispute resolution) will remain in effect.</p>
                </Section>

                <Section title="10. Disclaimer of warranties">
                    <p>CheerUp! is provided "as is" and "as available", without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Service will be uninterrupted, error-free, or always available.</p>
                </Section>

                <Section title="11. Limitation of liability">
                    <p>To the fullest extent permitted by law, CheerUp! and its team will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenues, data, or goodwill, arising from your use of the Service. Our total liability for any claim relating to the Service will not exceed the greater of (a) the amount you paid us in the 12 months before the claim, or (b) USD 100.</p>
                </Section>

                <Section title="12. Indemnity">
                    <p>You agree to defend and indemnify CheerUp! against claims arising from Your Content, your misuse of the Service, or your violation of these Terms or any law.</p>
                </Section>

                <Section title="13. Governing law and disputes">
                    <p>These Terms are governed by the laws of the jurisdiction in which CheerUp! is operated, without regard to conflict-of-laws principles. Any dispute will be resolved in the competent courts of that jurisdiction, unless applicable local consumer laws grant you different rights.</p>
                </Section>

                <Section title="14. Changes to these Terms">
                    <p>We may update these Terms from time to time. When we make meaningful changes, we'll update the effective date above and, when appropriate, notify you in the app. Continued use after changes means you accept the updated Terms.</p>
                </Section>

                <Section title="15. Contact">
                    <p>Questions or concerns? Reach us at <a href="mailto:query@cheerup-ai.com" className="text-primary underline">query@cheerup-ai.com</a>. We read every message.</p>
                </Section>

                <div className="mt-16 p-8 rounded-3xl bg-accent/40 border border-border">
                    <p className="font-heading text-lg font-[700] mb-2">A little note</p>
                    <p className="text-muted-foreground leading-relaxed">Terms exist to keep everyone safe, including you. If any part felt unclear, tell us — we'd rather rewrite a paragraph than leave you wondering.</p>
                </div>
                <FeedbackFooter />
            </motion.article>
        </div>
    );
}
